import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { IDLE_TIMEOUT_MS, IDLE_CHECK_INTERVAL_MS } from '@shared/sessionPolicy';
import {
  broadcastSignedOut,
  markActivity,
  pingIfDue,
  readLastActivity,
  SIGNED_OUT_BROADCAST_KEY,
} from '@/lib/sessionActivity';

/**
 * Sign the user out after 60 minutes with no interaction, and tell them why.
 *
 * THIS IS NOT THE SECURITY CONTROL. `enforceSessionTimeouts` on the server is;
 * it runs on every authenticated /api request and does not care whether this
 * component exists. What this adds is the half the server cannot do: end the
 * session the moment it lapses rather than whenever the next request happens
 * to fire, and land the user on the login page with an explanation instead of
 * a screen that silently stops working.
 *
 * Renders nothing. Mounted once, in the authenticated branch of AppContent, so
 * it survives navigation and never runs on the login or 2FA screens.
 */

// Everything a person does that a page can see. `mousemove` is in the list —
// reading a long report with a hand on the mouse is not idleness — which is
// why the listener is throttled rather than writing on every event.
const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
  'mousemove',
] as const;

/** Longest gap between two localStorage writes while the user is active. */
const ACTIVITY_WRITE_THROTTLE_MS = 5_000;

export function IdleTimeout() {
  const { logout } = useAuth();
  // Sign-out is a redirect and a network call; a second one is a race, not a
  // retry. A ref rather than state — nothing here renders.
  const signedOutRef = useRef(false);

  useEffect(() => {
    // Mounting means a page load or a fresh sign-in, both of which are the
    // user doing something. Start the clock now rather than inheriting a
    // stale timestamp from a previous session in this browser.
    markActivity();

    const signOut = (broadcast: boolean) => {
      if (signedOutRef.current) return;
      signedOutRef.current = true;
      if (broadcast) broadcastSignedOut();
      logout({ reason: 'idle' });
    };

    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      // Check BEFORE recording. Otherwise the first click of someone returning
      // to a machine that has been idle for an hour resets the clock, and the
      // tick that was going to sign them out never sees the gap — the exact
      // hole a laptop waking from sleep opens, since timers are frozen while
      // it sleeps and the user's hand lands first.
      if (now - readLastActivity() >= IDLE_TIMEOUT_MS) return signOut(true);
      if (now - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
      lastWrite = now;
      markActivity(now);
    };

    const tick = () => {
      const idleFor = Date.now() - readLastActivity();
      if (idleFor >= IDLE_TIMEOUT_MS) return signOut(true);
      pingIfDue(idleFor);
    };

    // A hidden tab's timers are throttled to roughly once a minute, and a
    // sleeping machine's do not run at all. Re-check the moment the tab is
    // looked at again, so the answer is right when it is first visible.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };

    // Another tab timed out (or was signed out); follow it rather than waiting
    // for this tab's next request to 401.
    const onStorage = (event: StorageEvent) => {
      if (event.key === SIGNED_OUT_BROADCAST_KEY && event.newValue) signOut(false);
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true, capture: true });
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('storage', onStorage);
    const timer = window.setInterval(tick, IDLE_CHECK_INTERVAL_MS);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity, { capture: true });
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('storage', onStorage);
      window.clearInterval(timer);
    };
  }, [logout]);

  return null;
}
